import type {
  QuoteTaskGroupPublic,
  WorkflowStepPublic,
  WorkPackageTaskPublic,
  WorkPackageWithTasksPublic,
  WorkTaskStatus,
} from "@common/client"
import { toNumberOrNull } from "@common/lib/number"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import {
  type SortDirection,
  type SortValue,
  sortRows,
} from "@/routes/_layout/maintenance-programmes/-lib/sort"
import { fullName } from "@/routes/_layout/quotes_/$quoteId/-components/user-picker"
import { aircraftTypesSchema } from "@/routes/_layout/quotes_/$quoteId/-lib/aircraft-types"
import { TASK_STATUSES } from "@/routes/_layout/work-orders/-lib/status"
import { squawkSearchFields } from "./squawks"
import { tabSchema, taskTabSchema } from "./tabs"

export const ITEM_SORTS = [
  "task",
  "description",
  "assignee",
  "task_group",
  "hours",
  "updated",
] as const

const itemSortSchema = z.enum(ITEM_SORTS)

export type ItemSort = z.infer<typeof itemSortSchema>

export type ItemSlot = ItemSort | "status"

export const COLUMNS = ["assignee", "task_group", "hours", "updated"] as const

const columnSchema = z.enum(COLUMNS)

export type Column = z.infer<typeof columnSchema>

const DEFAULT_COLUMNS: Column[] = ["assignee", "hours", "updated"]

export const COLUMN_LABELS: Record<Column, string> = {
  assignee: "Assignee",
  task_group: "Task group",
  hours: "Hours",
  updated: "Updated",
}

export const UNASSIGNED = "unassigned"

export const UNGROUPED = "ungrouped"

export const NO_DEPARTMENT = "no_department"

export const GROUPINGS = [
  "phase",
  "status",
  "assignee",
  "step",
  "task_group",
] as const

const groupingSchema = z.enum(GROUPINGS)

export type Grouping = z.infer<typeof groupingSchema>

export const GROUPING_LABELS: Record<Grouping, string> = {
  phase: "Phase",
  status: "Status",
  assignee: "Assignee",
  step: "Step",
  task_group: "Task group",
}

export const workOrderSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  pane: taskTabSchema.optional().catch(undefined),
  status: z.array(z.enum(TASK_STATUSES)).optional().catch(undefined),
  assignee: z.array(z.string()).optional().catch(undefined),
  step: z.array(z.string()).optional().catch(undefined),
  task_group: z.array(z.string()).optional().catch(undefined),
  department: z.array(z.string()).optional().catch(undefined),
  group: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  subgroup: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: itemSortSchema.optional().catch(undefined),
  dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  ...squawkSearchFields,
  aircraft_types: aircraftTypesSchema.optional().catch(undefined),
})

export type WorkOrderSearch = z.infer<typeof workOrderSearchSchema>

export type ItemsDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
}

export type ItemFilterValues = {
  status?: WorkTaskStatus[]
  assignee?: string[]
  step?: string[]
  task_group?: string[]
  department?: string[]
}

export const NO_ITEM_FILTERS: ItemFilterValues = {
  status: undefined,
  assignee: undefined,
  step: undefined,
  task_group: undefined,
  department: undefined,
}

const SUBGROUPS: Partial<Record<Grouping, Grouping>> = { phase: "status" }

export function groupingsOf(
  workOrder: WorkPackageWithTasksPublic,
  phasesEnabled: boolean,
  groupsReadable: boolean,
): readonly Grouping[] {
  return GROUPINGS.filter(
    (grouping) =>
      (grouping !== "phase" || phasesEnabled) &&
      (grouping !== "step" || workOrder.workflow) &&
      (grouping !== "task_group" || groupsReadable),
  )
}

function defaultGroup(
  workOrder: WorkPackageWithTasksPublic,
  phasesEnabled: boolean,
): Grouping {
  return phasesEnabled && workOrder.active_phases.length > 0
    ? "phase"
    : "status"
}

export function displayOf(
  search: WorkOrderSearch,
  workOrder: WorkPackageWithTasksPublic,
  phasesEnabled: boolean,
  groupsReadable: boolean,
): ItemsDisplay {
  const groupings = groupingsOf(workOrder, phasesEnabled, groupsReadable)
  const offered = (grouping: Grouping | "none" | undefined) =>
    grouping && grouping !== "none" && groupings.includes(grouping)
      ? grouping
      : undefined
  const group =
    search.group === "none"
      ? undefined
      : (offered(search.group) ?? defaultGroup(workOrder, phasesEnabled))
  const subgroup = offered(search.subgroup ?? (group && SUBGROUPS[group]))
  const columns = search.columns ?? DEFAULT_COLUMNS
  return {
    group,
    subgroup: group && subgroup !== group ? subgroup : undefined,
    columns: columnsOffered(groupsReadable).filter((column) =>
      columns.includes(column),
    ),
  }
}

export function columnsOffered(groupsReadable: boolean): Column[] {
  return COLUMNS.filter((column) => column !== "task_group" || groupsReadable)
}

export function columnsSearch(
  columns: Column[],
): Pick<WorkOrderSearch, "columns"> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

export function groupingSearch(
  next: Display<Grouping>,
  workOrder: WorkPackageWithTasksPublic,
  phasesEnabled: boolean,
): Pick<WorkOrderSearch, "group" | "subgroup"> {
  const natural = next.group && SUBGROUPS[next.group]
  return {
    group:
      next.group === defaultGroup(workOrder, phasesEnabled)
        ? undefined
        : (next.group ?? "none"),
    subgroup:
      next.subgroup === natural
        ? undefined
        : (next.subgroup ?? (natural ? "none" : undefined)),
  }
}

export function filtersOf(
  search: WorkOrderSearch,
  workOrder: WorkPackageWithTasksPublic,
  groupsReadable: boolean,
  departmentsReadable: boolean,
): ItemFilterValues {
  return {
    status: search.status,
    assignee: search.assignee,
    step: workOrder.workflow ? search.step : undefined,
    task_group: groupsReadable ? search.task_group : undefined,
    department: departmentsReadable ? search.department : undefined,
  }
}

export function hasItemFilters(values: ItemFilterValues): boolean {
  return Boolean(
    values.status?.length ||
      values.assignee?.length ||
      values.step?.length ||
      values.task_group?.length ||
      values.department?.length,
  )
}

export function departmentIdOf(
  workOrder: WorkPackageWithTasksPublic,
  task: WorkPackageTaskPublic,
): string | null {
  return task.department_id ?? workOrder.department_id
}

function matchesFilters(
  workOrder: WorkPackageWithTasksPublic,
  task: WorkPackageTaskPublic,
  { status, assignee, step, task_group, department }: ItemFilterValues,
  taskGroups: QuoteTaskGroupPublic[],
): boolean {
  const stepId = task.workflow_step_id ?? ""
  const taskGroup = taskGroups.find(({ id }) => id === task.task_group_id)
  return (
    (!status?.length || status.includes(task.status)) &&
    (!assignee?.length || assignee.includes(task.assignee_id ?? UNASSIGNED)) &&
    (!step?.length || step.includes(stepId)) &&
    (!task_group?.length || task_group.includes(taskGroup?.id ?? UNGROUPED)) &&
    (!department?.length ||
      department.includes(departmentIdOf(workOrder, task) ?? NO_DEPARTMENT))
  )
}

export function stepOf(
  workOrder: WorkPackageWithTasksPublic,
  task: WorkPackageTaskPublic,
): WorkflowStepPublic | undefined {
  return workOrder.workflow?.active_steps.find(
    ({ id }) => id === task.workflow_step_id,
  )
}

export type HoursUse = {
  erp: number
  planned: number
  ratio: number
  over: boolean
}

export function hoursUse(task: WorkPackageTaskPublic): HoursUse | null {
  const planned = toNumberOrNull(task.estimated_hrs)
  if (!planned) return null
  const erp = toNumberOrNull(task.actual_hrs) ?? 0
  return { erp, planned, ratio: erp / planned, over: erp > planned }
}

function sortValue(
  task: WorkPackageTaskPublic,
  sort: ItemSort,
  taskGroups: QuoteTaskGroupPublic[],
): SortValue {
  switch (sort) {
    case "task":
      return task.number
    case "description":
      return task.description
    case "assignee":
      return task.assignee ? fullName(task.assignee) : null
    case "task_group":
      return (
        taskGroups.find(({ id }) => id === task.task_group_id)?.position ?? null
      )
    case "hours":
      return hoursUse(task)?.ratio ?? null
    case "updated":
      return task.updated_at
  }
}

export function shownTasks(
  workOrder: WorkPackageWithTasksPublic,
  search: WorkOrderSearch,
  taskGroups: QuoteTaskGroupPublic[],
  groupsReadable: boolean,
  departmentsReadable: boolean,
): WorkPackageTaskPublic[] {
  const filters = filtersOf(
    search,
    workOrder,
    groupsReadable,
    departmentsReadable,
  )
  return sortedTasks(workOrder, search.sort, search.dir, taskGroups).filter(
    (task) => matchesFilters(workOrder, task, filters, taskGroups),
  )
}

function sortedTasks(
  workOrder: WorkPackageWithTasksPublic,
  sort: ItemSort | undefined,
  dir: SortDirection | undefined,
  taskGroups: QuoteTaskGroupPublic[],
): WorkPackageTaskPublic[] {
  return sortRows(
    workOrder.tasks ?? [],
    (task) => sortValue(task, sort ?? "task", taskGroups),
    dir ?? "asc",
  )
}
